import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog.js';
import { Button } from '@/components/ui/button.js';
import { Textarea } from '@/components/ui/textarea.js';
import { Input } from '@/components/ui/input.js';
import { Badge } from '@/components/ui/badge.js';
import { Tag, Pin, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import type { Memory } from '@/types/api.js';

interface ViewMemoryModalProps {
  memory: Memory | null;
  onClose: () => void;
  onUpdate: () => void;
  onDelete: () => void;
}

export function ViewMemoryModal({ memory, onClose, onUpdate, onDelete }: ViewMemoryModalProps) {
  const { api } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form when memory changes
  useState(() => {
    if (memory) {
      setContent(memory.content);
      setTags(memory.tags?.join(', ') || '');
      setIsPinned(memory.metadata?.pinned || false);
      setIsEditing(false);
    }
  });

  if (!memory) return null;

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const parsedTags = tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      await api.updateMemory(memory.id, {
        content: content.trim(),
        tags: parsedTags.length > 0 ? parsedTags : undefined,
        pinned: isPinned,
      });

      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to update memory:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this memory?')) return;

    try {
      await api.deleteMemory(memory.id);
      onDelete();
      onClose();
    } catch (error) {
      console.error('Failed to delete memory:', error);
    }
  };

  const handleTogglePin = async () => {
    try {
      const newPinned = !isPinned;
      setIsPinned(newPinned);
      await api.updateMemory(memory.id, { pinned: newPinned });
      onUpdate();
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      setIsPinned(!isPinned); // Revert on error
    }
  };

  return (
    <Dialog open={!!memory} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{isEditing ? 'Edit Memory' : 'View Memory'}</DialogTitle>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleTogglePin}
                className={cn('h-8 w-8', isPinned && 'text-primary')}
              >
                <Pin className={cn('h-4 w-4', isPinned && 'fill-primary')} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDelete}
                className="h-8 w-8 text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {isEditing ? (
            <>
              {/* Editing mode */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Content</label>
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="min-h-[120px] resize-none"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Tags
                </label>
                <Input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="comma-separated tags"
                />
              </div>
            </>
          ) : (
            <>
              {/* View mode */}
              <div className="space-y-2">
                <p className="text-sm text-foreground leading-relaxed">{memory.content}</p>
              </div>

              {memory.tags && memory.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {memory.tags.map((tag, index) => (
                    <Badge key={index} variant="secondary">
                      <Tag className="h-3 w-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}

              <div className="pt-2 border-t border-border/50 text-xs text-muted-foreground space-y-1">
                <p>Created: {new Date(memory.createdAt).toLocaleString()}</p>
                <p>Updated: {new Date(memory.updatedAt).toLocaleString()}</p>
                {memory.metadata?.source && <p>Source: {memory.metadata.source}</p>}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!content.trim() || isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button onClick={() => setIsEditing(true)}>Edit</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
